import { PageContainer } from '@/components/PageContainer';
import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { Heading } from '@televet/kibble-ui/Heading';
import { Text } from '@televet/kibble-ui/Text';
import { Card } from '@televet/kibble-ui/Card';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex, Table, Box, VStack, HStack } from '@televet/kibble-ui/chakra';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createFileRoute,
  useNavigate,
  Outlet,
  useLocation,
} from '@tanstack/react-router';
import { Icon } from '@televet/kibble-ui/Icon';
import { useState } from 'react';

export const Route = createFileRoute('/_authenticated/eval-runs/$runId')({
  component: RouteComponent,
  beforeLoad: () => ({
    pageTitle: 'Agent Eval Details',
  }),
});

function RouteComponent() {
  const { runId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();

  // If on a child route (review page), render the outlet
  const isChildRoute = location.pathname.includes('/review/');
  if (isChildRoute) {
    return <Outlet />;
  }

  const [convPage, setConvPage] = useState(1);
  const [feedbackFilter, setFeedbackFilter] = useState<'all' | 'needs_help'>('all');
  const [selectedActionItemId, setSelectedActionItemId] = useState<string | null>(null);

  const { data, isLoading } = useQuery(
    trpc.agentEvalRun.getRun.queryOptions({
      id: runId,
      conversationPage: convPage,
      conversationLimit: 10,
      feedbackFilter,
    })
  );

  const { data: actionItemsData, isLoading: actionItemsLoading } = useQuery(
    trpc.agentEvalRun.listActionItems.queryOptions({ evalRunId: runId })
  );

  const { mutate: completeRun, isPending: isCompleting } = useMutation(
    trpc.agentEvalRun.completeRun.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [['agentEvalRun', 'getRun']],
        });
      },
    })
  );

  const { mutate: generateActionItems, isPending: isGenerating } = useMutation(
    trpc.agentEvalRun.generateActionItems.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [['agentEvalRun', 'listActionItems']],
        });
      },
    })
  );

  const { mutate: updateActionItem } = useMutation(
    trpc.agentEvalRun.updateActionItem.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [['agentEvalRun', 'listActionItems']],
        });
      },
    })
  );

  const { data: nextUnreviewed } = useQuery(
    trpc.agentEvalRun.getNextUnreviewed.queryOptions({ evalRunId: runId })
  );

  const { data: actionItemDetail, isLoading: actionItemDetailLoading } = useQuery(
    trpc.agentEvalRun.getActionItem.queryOptions(
      { id: selectedActionItemId! },
      { enabled: !!selectedActionItemId }
    )
  );

  if (isLoading) {
    return (
      <PageContainer>
        <Text>Loading...</Text>
      </PageContainer>
    );
  }

  if (!data) {
    return (
      <PageContainer>
        <VStack gap={4}>
          <Text variant="danger">Agent eval not found</Text>
          <Button onClick={() => navigate({ to: '/eval-runs' })}>
            Back to Agent Evals
          </Button>
        </VStack>
      </PageContainer>
    );
  }

  const { run, stats, conversations, conversationPagination } = data;

  const formatDate = (date: string | Date) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionItemTagVariant = (status: string) => {
    switch (status) {
      case 'Done':
        return 'success';
      case 'InProgress':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={6}>
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate({ to: '/eval-runs' })}
          >
            ← Back
          </Button>
          <HStack gap={2}>
            {run.status === 'InProgress' && (
              <Button
                size="sm"
                variant="primaryAccent"
                onClick={() => completeRun({ id: runId })}
                loading={isCompleting}
                loadingText="Completing..."
              >
                Complete Eval
              </Button>
            )}
          </HStack>
        </Flex>

        {/* Run Info */}
        <Card borderColor="border.default" rounded="lg">
          <Box px={6} pt={6} pb={6}>
            <Flex direction="column" gap={3}>
              <Flex align="center" gap={3}>
                <Heading size="md">{run.name}</Heading>
                <Tag
                  label={run.status}
                  variant={run.status === 'Completed' ? 'success' : 'info'}
                />
                <Tag
                  label={run.type === 'EndUser' ? 'End User' : 'Internal'}
                  variant="default"
                />
              </Flex>
              <HStack gap={4} fontSize="sm" color="text.subtle">
                <Flex align="center" gap={1}>
                  Agent:{' '}
                  <Tag label={run.agent.name} variant="info" />
                </Flex>
                <Text>
                  Created by:{' '}
                  {run.createdBy
                    ? `${run.createdBy.firstName ?? ''} ${run.createdBy.lastName ?? ''}`.trim() ||
                      run.createdBy.email
                    : 'System'}
                </Text>
                <Text>Created: {formatDate(run.createdAt)}</Text>
                {run.completedAt && (
                  <Text>Completed: {formatDate(run.completedAt)}</Text>
                )}
              </HStack>
            </Flex>
          </Box>
        </Card>

        {/* Stats Bar */}
        <HStack gap={4}>
          <Card flex="1" borderColor="border.default" rounded="lg">
            <Box py={3} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold">
                {stats.total}
              </Text>
              <Text fontSize="xs" variant="subtle">
                Total
              </Text>
            </Box>
          </Card>
          <Card flex="1" borderColor="border.default" rounded="lg">
            <Box py={3} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" variant="success">
                {stats.reviewed}
              </Text>
              <Text fontSize="xs" variant="subtle">
                Reviewed
              </Text>
            </Box>
          </Card>
          <Card flex="1" borderColor="border.default" rounded="lg">
            <Box py={3} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" variant="danger">
                {stats.needsHelp}
              </Text>
              <Text fontSize="xs" variant="subtle">
                Bad
              </Text>
            </Box>
          </Card>
          <Card flex="1" borderColor="border.default" rounded="lg">
            <Box py={3} textAlign="center">
              <Text fontSize="2xl" fontWeight="bold" variant="warning">
                {stats.remaining}
              </Text>
              <Text fontSize="xs" variant="subtle">
                Remaining
              </Text>
            </Box>
          </Card>
        </HStack>

        {/* Conversations Section */}
        <Flex direction="column" gap={3}>
          <Flex justify="space-between" align="center">
            <HStack gap={3}>
              <Heading size="md">Conversations</Heading>
              <HStack gap={1}>
                <Button
                  size="xs"
                  variant={feedbackFilter === 'all' ? 'primary' : 'secondary'}
                  onClick={() => { setFeedbackFilter('all'); setConvPage(1); }}
                >
                  All
                </Button>
                <Button
                  size="xs"
                  variant={feedbackFilter === 'needs_help' ? 'primaryDestructive' : 'secondaryDestructive'}
                  onClick={() => { setFeedbackFilter('needs_help'); setConvPage(1); }}
                  iconName="thumbsDown"
                >
                  Bad
                </Button>
              </HStack>
            </HStack>
            {nextUnreviewed?.conversationId && (
              <Button
                size="sm"
                variant="primary"
                onClick={() =>
                  navigate({
                    to: `/eval-runs/${runId}/review/${nextUnreviewed.conversationId}`,
                  })
                }
              >
                Start Reviewing
              </Button>
            )}
          </Flex>
          <Card borderColor="border.default" rounded="lg" overflow="hidden">
              <Box overflowX="auto">
                <Table.Root size="sm" interactive width="100%">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader width="40px"></Table.ColumnHeader>
                      <Table.ColumnHeader>Summary</Table.ColumnHeader>
                      <Table.ColumnHeader>Rating</Table.ColumnHeader>
                      <Table.ColumnHeader>Rated By</Table.ColumnHeader>
                      <Table.ColumnHeader>Comment</Table.ColumnHeader>
                      <Table.ColumnHeader>Created</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {conversations.map((c) => (
                      <Table.Row
                        key={c.id}
                        cursor="pointer"
                        onClick={() =>
                          navigate({
                            to: `/eval-runs/${runId}/review/${c.conversationId}`,
                          })
                        }
                      >
                        <Table.Cell>
                          {c.reviewed ? (
                            <Icon
                              name="checkmarkCircle"
                              color="text.success"
                              size="sm"
                            />
                          ) : (
                            <Icon name="minus" color="text.subtle" />
                          )}
                        </Table.Cell>
                        <Table.Cell fontSize="sm">
                          {c.conversation.aiSummary
                            ? c.conversation.aiSummary.substring(0, 80) +
                              (c.conversation.aiSummary.length > 80
                                ? '...'
                                : '')
                            : 'No summary'}
                        </Table.Cell>
                        <Table.Cell>
                          {c.feedback ? (
                            c.feedback.rating === 1 ? (
                              <Icon
                                name="thumbsUp"
                                color="text.success"
                                size="sm"
                              />
                            ) : (
                              <Icon
                                name="thumbsDown"
                                color="text.danger"
                                size="sm"
                              />
                            )
                          ) : (
                            <Text fontSize="sm" variant="subtle">
                              -
                            </Text>
                          )}
                        </Table.Cell>
                        <Table.Cell fontSize="sm" color="text.subtle">
                          {c.feedback?.user
                            ? `${c.feedback.user.firstName ?? ''} ${c.feedback.user.lastName ?? ''}`.trim() ||
                              c.feedback.user.email
                            : '-'}
                        </Table.Cell>
                        <Table.Cell fontSize="sm" color="text.subtle">
                          {c.feedback?.comment
                            ? c.feedback.comment.substring(0, 40) +
                              (c.feedback.comment.length > 40 ? '...' : '')
                            : '-'}
                        </Table.Cell>
                        <Table.Cell fontSize="sm" color="text.subtle">
                          {formatDate(c.conversation.createdAt)}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
              {conversationPagination.totalPages > 1 && (
                <Flex
                  justify="space-between"
                  align="center"
                  px={4}
                  py={2}
                  borderTop="1px solid"
                  borderColor="border.default"
                >
                  <Text fontSize="xs" variant="subtle">
                    {conversationPagination.total} conversations
                  </Text>
                  <Flex gap={2} align="center">
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={!conversationPagination.hasPrev}
                      onClick={() => setConvPage(convPage - 1)}
                    >
                      Prev
                    </Button>
                    <Text fontSize="xs" variant="subtle">
                      {conversationPagination.page}/{conversationPagination.totalPages}
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      disabled={!conversationPagination.hasNext}
                      onClick={() => setConvPage(convPage + 1)}
                    >
                      Next
                    </Button>
                  </Flex>
                </Flex>
              )}
          </Card>
        </Flex>

        {/* Action Items Section */}
        <Flex direction="column" gap={3}>
          <Flex justify="space-between" align="center">
            <Heading size="md">Action Items</Heading>
            <HStack gap={2}>
              {actionItemsData?.actionItems &&
                actionItemsData.actionItems.length > 0 && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      const items = actionItemsData.actionItems;
                      const escape = (v: string) =>
                        `"${v.replace(/"/g, '""')}"`;
                      const rows = [
                        ['Title', 'Description', 'Category', 'Status'].join(','),
                        ...items.map((item) =>
                          [
                            escape(item.title),
                            escape(item.description),
                            escape(item.category),
                            escape(item.status),
                          ].join(',')
                        ),
                      ];
                      const blob = new Blob([rows.join('\n')], {
                        type: 'text/csv',
                      });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `action-items-${run.name.replace(/\s+/g, '-').toLowerCase()}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    iconName="download"
                  >
                    Export CSV
                  </Button>
                )}
              <Button
                size="sm"
                variant="primaryAccent"
                onClick={() => generateActionItems({ evalRunId: runId })}
                disabled={stats.needsHelp === 0}
                loading={isGenerating}
                loadingText="Generating..."
                iconName="magic"
              >
                Generate Action Items
              </Button>
            </HStack>
          </Flex>
          <Card borderColor="border.default" rounded="lg" overflow="hidden">
              {actionItemsData?.actionItems &&
              actionItemsData.actionItems.length > 0 ? (
                <Table.Root size="sm" width="100%">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Title</Table.ColumnHeader>
                      <Table.ColumnHeader textAlign="center">
                        Instances
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>Category</Table.ColumnHeader>
                      <Table.ColumnHeader>Status</Table.ColumnHeader>
                      <Table.ColumnHeader width="200px">
                        Actions
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {actionItemsData.actionItems.map((item) => (
                      <Table.Row
                        key={item.id}
                        cursor="pointer"
                        bg={selectedActionItemId === item.id ? 'bg.subtle' : undefined}
                        onClick={() => setSelectedActionItemId(
                          selectedActionItemId === item.id ? null : item.id
                        )}
                      >
                        <Table.Cell>
                          <VStack align="flex-start" gap={1}>
                            <Text fontWeight="medium" fontSize="sm">
                              {item.title}
                            </Text>
                            <Text fontSize="xs" variant="subtle">
                              {item.description.substring(0, 100)}
                              {item.description.length > 100 ? '...' : ''}
                            </Text>
                          </VStack>
                        </Table.Cell>
                        <Table.Cell textAlign="center">
                          <Tag
                            label={
                              stats.needsHelp > 0
                                ? `${item.feedbackCount} (${Math.round((item.feedbackCount / stats.needsHelp) * 100)}%)`
                                : String(item.feedbackCount)
                            }
                            variant="default"
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Tag label={item.category} variant="default" />
                        </Table.Cell>
                        <Table.Cell>
                          <Tag
                            label={item.status}
                            variant={getActionItemTagVariant(item.status)}
                          />
                        </Table.Cell>
                        <Table.Cell onClick={(e) => e.stopPropagation()}>
                          <HStack gap={1}>
                            {item.status !== 'Open' && (
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() =>
                                  updateActionItem({
                                    id: item.id,
                                    status: 'Open',
                                  })
                                }
                              >
                                Open
                              </Button>
                            )}
                            {item.status !== 'InProgress' && (
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() =>
                                  updateActionItem({
                                    id: item.id,
                                    status: 'InProgress',
                                  })
                                }
                              >
                                In Progress
                              </Button>
                            )}
                            {item.status !== 'Done' && (
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() =>
                                  updateActionItem({
                                    id: item.id,
                                    status: 'Done',
                                  })
                                }
                              >
                                Done
                              </Button>
                            )}
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              ) : (
                <Flex justify="center" py={8}>
                  <Text variant="subtle" fontSize="sm">
                    {stats.needsHelp > 0
                      ? 'Click "Generate Action Items" to create AI-grouped items from negative feedback.'
                      : 'No negative feedback to generate action items from.'}
                  </Text>
                </Flex>
              )}
          </Card>

          {/* Action Item Detail */}
          {selectedActionItemId && (
            <Card borderColor="border.default" rounded="lg">
              {actionItemDetailLoading ? (
                <Box p={6}>
                  <Text variant="subtle">Loading...</Text>
                </Box>
              ) : actionItemDetail ? (
                <>
                  <Box px={6} pt={6}>
                    <Flex direction="column" gap={2}>
                      <Flex justify="space-between" align="center">
                        <Heading size="sm">{actionItemDetail.actionItem.title}</Heading>
                        <HStack gap={2}>
                          <Tag
                            label={actionItemDetail.actionItem.category}
                            variant="default"
                          />
                          <Tag
                            label={actionItemDetail.actionItem.status}
                            variant={getActionItemTagVariant(actionItemDetail.actionItem.status)}
                          />
                        </HStack>
                      </Flex>
                      <Text fontSize="sm" variant="subtle">
                        {actionItemDetail.actionItem.description}
                      </Text>
                    </Flex>
                  </Box>
                  <Box px={6} pb={6}>
                    <Flex direction="column" gap={3}>
                      <Heading size="xs">
                        Linked Conversations ({actionItemDetail.linkedConversations.length})
                      </Heading>
                      <VStack gap={2} align="stretch">
                        {actionItemDetail.linkedConversations.map((lc) => (
                          <Card
                            key={lc.feedback.id}
                            borderColor="border.default"
                            rounded="md"
                            cursor="pointer"
                            _hover={{ bg: 'bg.subtle' }}
                            onClick={() =>
                              navigate({
                                to: `/eval-runs/${runId}/review/${lc.feedback.entityId}`,
                              })
                            }
                          >
                            <Box py={3} px={4}>
                              <Flex direction="column" gap={1}>
                                <Flex justify="space-between" align="center">
                                  <Text fontSize="sm" fontWeight="medium">
                                    {lc.conversation?.aiSummary
                                      ? lc.conversation.aiSummary.substring(0, 120) +
                                        (lc.conversation.aiSummary.length > 120 ? '...' : '')
                                      : 'No summary'}
                                  </Text>
                                  <Icon name="thumbsDown" color="text.danger" size="xs" />
                                </Flex>
                                {lc.feedback.comment && (
                                  <Text fontSize="xs" variant="subtle">
                                    {lc.feedback.comment.substring(0, 150)}
                                    {lc.feedback.comment.length > 150 ? '...' : ''}
                                  </Text>
                                )}
                                {lc.conversation && (
                                  <Text fontSize="xs" variant="subtle">
                                    {formatDate(lc.conversation.createdAt)}
                                  </Text>
                                )}
                              </Flex>
                            </Box>
                          </Card>
                        ))}
                      </VStack>
                    </Flex>
                  </Box>
                </>
              ) : null}
            </Card>
          )}
        </Flex>
      </Flex>
    </PageContainer>
  );
}
